import {
  getConflictingUidDocuments,
  getDocumentList,
  hasUIDField,
  isMissingUID,
} from "@prismicio/editor-fields"
import type { AlertConfig } from "@prismicio/editor-ui"
import type { Document as DocumentT } from "@prismicio/types-internal/lib/content"
import type { CustomType } from "@prismicio/types-internal/lib/customtypes"
import { captureException } from "@sentry/react"

import { env } from "../../app/env"
import { baseUrl } from "../../app/services/core"
import { actionAlert, type PendingAction } from "./HeaderButtons/HeaderButtons"

interface validateUIDProps {
  content: DocumentT
  customType: CustomType
  documentId?: string
  languageId: string
  showAlert: (config: AlertConfig) => void
  pendingAction: NonNullable<PendingAction>
  onRetry: () => void
}

export function validateUID(props: validateUIDProps) {
  const {
    content,
    customType,
    documentId,
    languageId,
    showAlert,
    pendingAction,
    onRetry,
  } = props

  if (!hasUIDField(customType)) return true

  if (isMissingUID({ content, customType })) {
    showAlert({
      title: `Oops! We couldn't ${pendingAction} your Document`,
      subtitle: `Hey there! It seems you left the UID field blank. Could you fill it in before trying to ${pendingAction}?`,
    })

    return false
  }

  const field = content["uid"]
  const fieldType = field?.__TYPE__

  // Should never get there:
  // - The model was verified to be of type UID.
  // - If there's no content, we get into the isMissingUID branch.
  // - If there is content of type UIDContent, we're good, proceed with the real check.
  // - If there is content but not of type UIDContent, it should have been removed from the document
  //   before this point and we would have gotten into the isMissingUID branch.
  if (fieldType !== "UIDContent") {
    captureException(
      new Error(
        `Bad UID state, content is non empty and of type ${fieldType ?? "null"}`,
      ),
    )
    return false
  }

  const uid = field.value

  return getDocumentList({
    baseUrl,
    authStrategy: env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
    uids: [uid],
  })
    .then((response) => {
      const conflictingDocuments = getConflictingUidDocuments({
        documentId: documentId,
        documentsWithUid: response.results,
        customTypeId: customType.id,
        languageId,
        uid,
      })

      if (conflictingDocuments.length > 0) {
        showAlert({
          title: `Oops! We couldn't ${pendingAction} your Document`,
          subtitle: `Oops! Looks like the value you entered as a UID is already taken. Mind trying a different one and trying to ${pendingAction} again?`,
        })
        return false
      }
      return true
    })
    .catch(() => {
      actionAlert({ action: pendingAction, showAlert, onRetry })
      return false
    })
}
